import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';

import {
  ACTION_FILTER_QUEUED,
  COMMAND_REQUEST_TYPE,
  DEVICE_STATUS,
  type CommandType,
} from './constants';
import {
  deviceCategoryListSchema,
  devicePageWireSchema,
  toDevice,
  type DeviceCategory,
  type DeviceListQuery,
  type DevicePage,
} from './schemas';

/**
 * Serialize a list query.
 *
 * Everything is server-side: search, category, the three filter groups, sorting and paging are
 * all query params. The filter groups go out **comma-separated** (not repeated params), which is
 * what this endpoint accepts.
 *
 * `expired` is deliberately dropped from `deviceStatus`: expiry is a subscription concern and has
 * no `deviceStatus` wire value — it is filtered through `subscriptionStatus` instead.
 */
function listParams(query: DeviceListQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.set('limit', String(query.limit));
  if (query.cursor) params.set('cursor', query.cursor);

  const search = query.search.trim();
  if (search) params.set('search', search);
  if (query.categoryId) params.set('category', query.categoryId);

  const statuses = query.deviceStatus.filter((status) => status !== DEVICE_STATUS.EXPIRED);
  if (statuses.length > 0) params.set('deviceStatus', statuses.join(','));
  if (query.subscriptionStatus.length > 0) {
    params.set('subscriptionStatus', query.subscriptionStatus.join(','));
  }
  if (query.queuedOnly) params.set('action', ACTION_FILTER_QUEUED);

  return params;
}

/**
 * One page of the grower's devices, already sorted by the backend (status group, then
 * last-active descending) and normalised into the domain shape.
 */
export async function fetchDevices(query: DeviceListQuery): Promise<DevicePage> {
  // The success envelope is `{ message, data: { devices, meta } }`; `apiClient.get` peels
  // `data`, leaving the page object.
  const payload = await apiClient.get<unknown>(endpoints.devices.list, {
    params: listParams(query),
  });
  const page = devicePageWireSchema.parse(payload ?? {});

  return {
    devices: page.devices.map(toDevice),
    hasNextPage: page.meta.hasNextPage,
    nextCursor: page.meta.nextCursor ?? null,
  };
}

/**
 * The grower's own device categories — the listing's quick-filter tabs.
 *
 * These are **per account**, which is why the tab strip is data-driven rather than a fixed
 * Diesel/Electric/Sensors set: the backend has no such classification.
 */
export async function fetchDeviceCategories(): Promise<DeviceCategory[]> {
  const payload = await apiClient.get<unknown>(endpoints.devices.categories);
  return deviceCategoryListSchema.parse(payload ?? []);
}

/**
 * Queue a Turn On / Turn Off command. No confirmation dialog — the AC is explicit that clicking
 * the power icon queues immediately (US-112439 §6). Responds 201.
 *
 * The request body uses SCREAMING_SNAKE (`TURN_ON`) while every response field is snake_case.
 */
export async function queueDeviceCommand(input: {
  deviceId: string;
  type: CommandType;
}): Promise<void> {
  await apiClient.post(endpoints.devices.commands(input.deviceId), {
    type: COMMAND_REQUEST_TYPE[input.type],
  });
}

/**
 * Cancel a queued command (US-112437 §9 banner Cancel).
 *
 * Needs the command's own id, not just the device's. When the server omitted it from
 * `queuedAction` there is nothing to call, so the caller must re-read the device rather than
 * pretend the cancellation succeeded.
 */
export async function cancelDeviceCommand(input: {
  deviceId: string;
  commandId: string;
}): Promise<void> {
  await apiClient.delete(endpoints.devices.command(input.deviceId, input.commandId));
}

/**
 * Set — or, with `null`, clear — the grower-assigned name. Returns the server's resulting
 * display name, which is what the header should show.
 */
export async function renameDevice(input: {
  deviceId: string;
  customName: string | null;
}): Promise<string> {
  const payload = await apiClient.patch<{ displayName?: string } | null>(
    endpoints.devices.rename(input.deviceId),
    { customName: input.customName },
  );
  return payload?.displayName ?? input.customName ?? '';
}
